import React from 'react';
import { Shield, Award, MapPin, Clock, CheckCircle } from 'lucide-react';
import { mockData } from '../data/mock';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const About = () => {
  return (
    <div className="min-h-screen pt-24 pb-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                JTS Alsace - Votre Expert Anti-Nuisibles
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Spécialiste dans le traitement professionnel des nuisibles en Alsace, 
                JTS Alsace met son expertise au service de votre tranquillité depuis plusieurs années.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Notre mission : protéger votre environnement avec des solutions efficaces, 
                respectueuses et durables. Disponibles 24h/24 et 7j/7, nous intervenons 
                rapidement pour résoudre tous vos problèmes de nuisibles.
              </p>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-blue-600 rounded-3xl transform -rotate-3" />
              <img
                src={mockData.images.technician}
                alt="Technicien JTS Alsace"
                className="relative rounded-3xl shadow-2xl object-cover w-full h-[400px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nos Valeurs
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Ce qui nous guide au quotidien pour vous offrir le meilleur service
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-gray-100 hover:border-blue-500">
              <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                <Shield className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Professionnalisme
              </h3>
              <p className="text-gray-600">
                Certification biocide et formation continue pour garantir la qualité de nos interventions.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-gray-100 hover:border-blue-500">
              <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                <Clock className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Réactivité
              </h3>
              <p className="text-gray-600">
                Disponibles 24/7 pour intervenir rapidement en cas d'urgence, partout en Alsace.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-gray-100 hover:border-blue-500">
              <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                <CheckCircle className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Efficacité
              </h3>
              <p className="text-gray-600">
                Des traitements éprouvés et un suivi rigoureux pour une élimination complète et durable.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-gray-100 hover:border-blue-500">
              <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                <Award className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Qualité
              </h3>
              <p className="text-gray-600">
                Utilisation de produits certifiés et respect des normes pour votre sécurité.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl p-12">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Certification & Expertise
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Shield className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">Certifié Biocide</h3>
                      <p className="text-gray-600">
                        Nous disposons de la certification biocide obligatoire pour l'utilisation 
                        professionnelle de produits de lutte contre les nuisibles.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Award className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">Formation Continue</h3>
                      <p className="text-gray-600">
                        Nos techniciens suivent régulièrement des formations pour rester 
                        à jour sur les meilleures techniques et réglementations.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">Expertise Locale</h3>
                      <p className="text-gray-600">
                        Basés à Rouffach, nous connaissons parfaitement les spécificités 
                        et problématiques de nuisibles en Alsace.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-12 text-white text-center">
                <div className="inline-block bg-white/10 backdrop-blur-sm rounded-full p-6 mb-6">
                  <Shield className="w-16 h-16 text-white" />
                </div>
                <h3 className="text-3xl font-bold mb-4">Certifié Biocide</h3>
                <p className="text-blue-100 mb-6">
                  Certification officielle pour l'utilisation professionnelle de produits biocides
                </p>
                <div className="inline-block bg-white text-blue-600 px-6 py-3 rounded-lg font-bold text-lg">
                  Expert Agréé
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Zone d'intervention */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Zone d'Intervention
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Nous intervenons dans toute la région Alsace
            </p>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-12 text-center">
            <MapPin className="w-16 h-16 text-blue-600 mx-auto mb-6" />
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Basés à Rouffach
            </h3>
            <p className="text-lg text-gray-600 mb-6">
              {mockData.contact.fullAddress}
            </p>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Depuis notre base située à Rouffach, nous couvrons l'ensemble de l'Alsace : 
              Colmar, Mulhouse, Strasbourg et toutes les communes environnantes. 
              Notre proximité nous permet d'intervenir rapidement où que vous soyez.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Questions Fréquentes
            </h2>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8">
            <Accordion type="single" collapsible className="w-full">
              {mockData.faqs.map((faq, index) => (
                <AccordionItem key={faq.id} value={`item-${index}`}>
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-blue-600">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
